import { lazy } from 'react';
import {
  Braces,
  KeyRound,
  Regex,
  Clock,
  FileCode2,
  Link2,
  Hash,
  StickyNote,
  Fingerprint,
  Shield,
  Compass,
  ListTree,
  Code2,
  CaseUpper,
  Calculator,
  Lock,
  GitCompareArrows,
  ArrowDownUp,
  Globe2,
  CalendarClock,
  Palette,
  KeySquare,
  Terminal,
  Network,
  FileJson2,
  Sparkles,
  AlarmClockCheck,
  FileCode,
  FileText,
  Paintbrush2,
  Braces as BracesIcon,
  Images,
  QrCode,
  ShieldCheck,
} from 'lucide-react';
import type { ToolDefinition } from '@/types/tool';

export const TOOLS: ToolDefinition[] = [
  {
    id: 'notes',
    name: 'Notes',
    description: 'Markdown scratchpad — type # for headings',
    keywords: ['notes', 'note', 'markdown', 'keep', 'memo', 'scratchpad', 'todo'],
    category: 'workspace',
    icon: StickyNote,
    component: lazy(() => import('@/features/notes/ui/NotesTool')),
  },
  {
    id: 'json',
    name: 'JSON Formatter',
    description: 'Format, minify, and validate JSON',
    keywords: ['json', 'format', 'pretty', 'minify', 'validate', 'parse'],
    category: 'formatters',
    icon: Braces,
    component: lazy(() => import('@/features/json/ui/JsonFormatter')),
  },
  {
    id: 'jwt',
    name: 'JWT Decoder',
    description: 'Inspect JWT header, payload, expiration',
    keywords: ['jwt', 'token', 'decode', 'auth', 'bearer'],
    category: 'inspectors',
    icon: KeyRound,
    component: lazy(() => import('@/features/jwt/ui/JwtDecoder')),
  },
  {
    id: 'regex',
    name: 'Regex Tester',
    description: 'Test regular expressions with live matches',
    keywords: ['regex', 'regexp', 'pattern', 'match', 'test'],
    category: 'inspectors',
    icon: Regex,
    component: lazy(() => import('@/features/regex/ui/RegexTester')),
  },
  {
    id: 'timestamp',
    name: 'Timestamp Converter',
    description: 'Unix timestamps ↔ human dates with timezone',
    keywords: ['timestamp', 'unix', 'epoch', 'date', 'time', 'convert'],
    category: 'converters',
    icon: Clock,
    component: lazy(() => import('@/features/timestamp/ui/TimestampConverter')),
  },
  {
    id: 'base64',
    name: 'Base64',
    description: 'Encode and decode Base64 strings',
    keywords: ['base64', 'encode', 'decode', 'b64'],
    category: 'encoders',
    icon: FileCode2,
    component: lazy(() => import('@/features/base64/ui/Base64Tool')),
  },
  {
    id: 'url',
    name: 'URL Encoder',
    description: 'Encode and decode URLs and query strings',
    keywords: ['url', 'uri', 'encode', 'decode', 'query', 'percent'],
    category: 'encoders',
    icon: Link2,
    component: lazy(() => import('@/features/url/ui/UrlTool')),
  },
  {
    id: 'uuid',
    name: 'UUID Generator',
    description: 'Generate one or many UUID v4 values',
    keywords: ['uuid', 'guid', 'v4', 'random', 'id'],
    category: 'generators',
    icon: Hash,
    component: lazy(() => import('@/features/uuid/ui/UuidGenerator')),
  },
  {
    id: 'hash',
    name: 'Hash Generator',
    description: 'MD5, SHA-1, SHA-256, SHA-384, SHA-512 digests',
    keywords: ['hash', 'md5', 'sha', 'sha1', 'sha256', 'sha512', 'digest', 'checksum'],
    category: 'encoders',
    icon: Fingerprint,
    component: lazy(() => import('@/features/hash/ui/HashGenerator')),
  },
  {
    id: 'hmac',
    name: 'HMAC Generator',
    description: 'HMAC-SHA256 / SHA512 with shared secret',
    keywords: ['hmac', 'sign', 'sha256', 'sha512', 'webhook', 'signature'],
    category: 'encoders',
    icon: Shield,
    component: lazy(() => import('@/features/hmac/ui/HmacGenerator')),
  },
  {
    id: 'urlparser',
    name: 'URL Parser',
    description: 'Break a URL into protocol, host, path, query, hash',
    keywords: ['url', 'parse', 'parser', 'host', 'protocol', 'pathname', 'query'],
    category: 'inspectors',
    icon: Compass,
    component: lazy(() => import('@/features/urlparser/ui/UrlParser')),
  },
  {
    id: 'querystring',
    name: 'Query String',
    description: 'Query string ↔ JSON object, bidirectional',
    keywords: ['query', 'string', 'json', 'params', 'urlsearchparams', 'qs'],
    category: 'converters',
    icon: ListTree,
    component: lazy(() => import('@/features/querystring/ui/QueryStringTool')),
  },
  {
    id: 'htmlentity',
    name: 'HTML Entity',
    description: 'Encode & decode HTML entities (&amp;, &#65;, …)',
    keywords: ['html', 'entity', 'entities', 'amp', 'escape', 'unescape'],
    category: 'encoders',
    icon: Code2,
    component: lazy(() => import('@/features/htmlentity/ui/HtmlEntityTool')),
  },
  {
    id: 'textcase',
    name: 'Case Converter',
    description: 'camelCase, snake_case, kebab-case, Title Case…',
    keywords: ['case', 'camel', 'pascal', 'snake', 'kebab', 'title', 'convert', 'naming'],
    category: 'text',
    icon: CaseUpper,
    component: lazy(() => import('@/features/textcase/ui/TextCaseConverter')),
  },
  {
    id: 'textcounter',
    name: 'Text Counter',
    description: 'Characters, words, lines, bytes, reading time',
    keywords: ['text', 'count', 'words', 'characters', 'bytes', 'lines', 'stats'],
    category: 'text',
    icon: Calculator,
    component: lazy(() => import('@/features/textcounter/ui/TextCounter')),
  },
  {
    id: 'password',
    name: 'Password Generator',
    description: 'Crypto-safe passwords with entropy estimate',
    keywords: ['password', 'generate', 'random', 'entropy', 'secure'],
    category: 'generators',
    icon: Lock,
    component: lazy(() => import('@/features/password/ui/PasswordGenerator')),
  },
  // Phase 4
  {
    id: 'diff',
    name: 'Diff Checker',
    description: 'Inline diff between two texts (lines / words / chars)',
    keywords: ['diff', 'compare', 'changes', 'patch', 'git'],
    category: 'inspectors',
    icon: GitCompareArrows,
    component: lazy(() => import('@/features/diff/ui/DiffChecker')),
  },
  {
    id: 'sorter',
    name: 'Text Sorter',
    description: 'Sort lines, dedupe, trim, natural ordering',
    keywords: ['sort', 'lines', 'dedupe', 'unique', 'shuffle'],
    category: 'text',
    icon: ArrowDownUp,
    component: lazy(() => import('@/features/sorter/ui/TextSorter')),
  },
  // Phase 5
  {
    id: 'cron',
    name: 'Cron Parser',
    description: 'Explain cron expressions in plain English',
    keywords: ['cron', 'crontab', 'schedule', 'job'],
    category: 'inspectors',
    icon: AlarmClockCheck,
    component: lazy(() => import('@/features/cron/ui/CronParser')),
  },
  {
    id: 'timezone',
    name: 'Timezone Converter',
    description: 'Compare a moment across multiple time zones',
    keywords: ['timezone', 'tz', 'utc', 'pst', 'gmt', 'convert'],
    category: 'converters',
    icon: Globe2,
    component: lazy(() => import('@/features/timezone/ui/TimezoneConverter')),
  },
  {
    id: 'datediff',
    name: 'Date Difference',
    description: 'Calendar-aware duration between two dates',
    keywords: ['date', 'difference', 'duration', 'between', 'days'],
    category: 'converters',
    icon: CalendarClock,
    component: lazy(() => import('@/features/datediff/ui/DateDiff')),
  },
  // Phase 6
  {
    id: 'color',
    name: 'Color Converter',
    description: 'HEX ↔ RGB ↔ HSL ↔ HSV + WCAG contrast',
    keywords: ['color', 'colour', 'hex', 'rgb', 'hsl', 'hsv', 'contrast', 'wcag'],
    category: 'converters',
    icon: Palette,
    component: lazy(() => import('@/features/color/ui/ColorConverter')),
  },
  {
    id: 'jwtgen',
    name: 'JWT Generator',
    description: 'Sign HS256 / HS384 / HS512 JWTs locally',
    keywords: ['jwt', 'generate', 'sign', 'hs256', 'hs512', 'token'],
    category: 'generators',
    icon: KeySquare,
    component: lazy(() => import('@/features/jwtgen/ui/JwtGenerator')),
  },
  // Phase 7
  {
    id: 'curl',
    name: 'cURL Builder',
    description: 'Build requests, export as curl / fetch / axios',
    keywords: ['curl', 'http', 'fetch', 'axios', 'api', 'request'],
    category: 'inspectors',
    icon: Terminal,
    component: lazy(() => import('@/features/curl/ui/CurlBuilder')),
  },
  {
    id: 'headers',
    name: 'HTTP Headers',
    description: 'Categorize and explain HTTP headers',
    keywords: ['http', 'headers', 'cookie', 'cors', 'auth', 'cache'],
    category: 'inspectors',
    icon: Network,
    component: lazy(() => import('@/features/headers/ui/HeaderInspector')),
  },
  {
    id: 'jsonschema',
    name: 'JSON Schema',
    description: 'Infer JSON Schema + TypeScript from sample JSON',
    keywords: ['json', 'schema', 'typescript', 'interface', 'generate'],
    category: 'generators',
    icon: FileJson2,
    component: lazy(() => import('@/features/jsonschema/ui/JsonSchemaGenerator')),
  },
  {
    id: 'mockdata',
    name: 'Mock Data',
    description: 'Generate fake names, emails, addresses, JSON, CSV',
    keywords: ['mock', 'fake', 'data', 'faker', 'seed', 'fixture'],
    category: 'generators',
    icon: Sparkles,
    component: lazy(() => import('@/features/mockdata/ui/MockData')),
  },
  // Phase 3
  {
    id: 'yaml',
    name: 'YAML ↔ JSON',
    description: 'Bidirectional YAML / JSON conversion',
    keywords: ['yaml', 'json', 'convert', 'yml'],
    category: 'converters',
    icon: BracesIcon,
    component: lazy(() => import('@/features/yaml/ui/YamlTool')),
  },
  {
    id: 'xml',
    name: 'XML Formatter',
    description: 'Format, minify, and validate XML',
    keywords: ['xml', 'format', 'pretty', 'minify', 'validate'],
    category: 'formatters',
    icon: FileCode,
    component: lazy(() => import('@/features/xml/ui/XmlFormatter')),
  },
  {
    id: 'sql',
    name: 'SQL Formatter',
    description: 'Pretty-print SQL for MySQL, Postgres, SQLite',
    keywords: ['sql', 'format', 'pretty', 'mysql', 'postgres', 'sqlite'],
    category: 'formatters',
    icon: FileCode,
    component: lazy(() => import('@/features/sql/ui/SqlFormatter')),
  },
  {
    id: 'html',
    name: 'HTML Formatter',
    description: 'Format and minify HTML markup',
    keywords: ['html', 'format', 'pretty', 'minify'],
    category: 'formatters',
    icon: FileText,
    component: lazy(() => import('@/features/html/ui/HtmlFormatter')),
  },
  {
    id: 'css',
    name: 'CSS Formatter',
    description: 'Format and minify CSS stylesheets',
    keywords: ['css', 'format', 'pretty', 'minify', 'style'],
    category: 'formatters',
    icon: Paintbrush2,
    component: lazy(() => import('@/features/css/ui/CssFormatter')),
  },
  {
    id: 'js',
    name: 'JS Formatter',
    description: 'Format and minify JavaScript snippets',
    keywords: ['js', 'javascript', 'format', 'pretty', 'minify', 'beautify'],
    category: 'formatters',
    icon: FileCode,
    component: lazy(() => import('@/features/js/ui/JsFormatter')),
  },
  {
    id: 'image',
    name: 'Image Converter',
    description: 'Convert PNG / JPEG / WebP / AVIF / ICO locally',
    keywords: [
      'image', 'convert', 'png', 'jpeg', 'jpg', 'webp', 'avif',
      'ico', 'icon', 'svg', 'gif', 'resize', 'compress',
    ],
    category: 'converters',
    icon: Images,
    component: lazy(() => import('@/features/image/ui/ImageConverter')),
  },
  {
    id: 'qr',
    name: 'QR Generator',
    description: 'Make text / URL / VietQR / Wi-Fi QR codes with style',
    keywords: [
      'qr', 'qrcode', 'qr code', 'generate', 'vietqr', 'banking',
      'momo', 'napas', 'wifi', 'url', 'scan', 'png', 'svg',
    ],
    category: 'generators',
    icon: QrCode,
    component: lazy(() => import('@/features/qr/ui/QrTool')),
  },
  {
    id: 'vault',
    name: 'Vault',
    description: 'Encrypted local store for secrets, env, SSH keys, server logins',
    keywords: [
      'vault', 'secret', 'secrets', 'env', 'environment', 'config',
      'ssh', 'key', 'password', 'login', 'credentials', 'safe',
      'encrypt', 'aes', 'kdf', 'pbkdf2', 'locker',
    ],
    category: 'security',
    icon: ShieldCheck,
    component: lazy(() => import('@/features/vault/ui/VaultTool')),
  },
];

export const TOOLS_BY_ID = Object.fromEntries(TOOLS.map((t) => [t.id, t]));

export function getTool(id: string) {
  return TOOLS_BY_ID[id];
}
